/**
 * Which durable write resolves a feed item — decided here, as data, so it can
 * be tested without a DOM.
 *
 * Three things in this routing are easy to get wrong and impossible to see once
 * wrong, which is why they live in a pure function with a test rather than
 * inline in a click handler:
 *
 *  1. Marking something DONE must never go through /v1/actions/{id}/reject.
 *     That endpoint reports the AI draft to /v1/ai/feedback as 'rejected'
 *     (see rateAiAction in lib/actions.ts), so routing a completion through it
 *     teaches the model that work the user actually did was a bad suggestion.
 *
 *  2. An `opportunity` has NO durable write available. Recommendations come
 *     from GET /v1/ml/next-best-actions, computed per request, with no write
 *     endpoint, and their ids are synthesised positionally in
 *     next-best-action.ts (`churn-${client_external_id}-${i}`). The old code
 *     POSTed that id to /v1/actions/{id}/reject behind a `.catch(() => {})`,
 *     where it could not match any action row — so the button looked like it
 *     worked and never persisted a thing. `sessionOnly` makes the UI say so.
 *
 *  3. A `followup` is one of two things. A sales-engine deal card
 *     (`deal-<id>`, with a Deal Coach key + fingerprint) is computed on read and
 *     is only ever hidden through POST /v1/sales/signals/state — the escalation
 *     endpoint rejects its id as not-a-signal, which is what "Could not mark
 *     done … nothing was saved" was. A mailbox signal (`signal-<uuid>`) is an
 *     email_signals row and goes through the escalation dismiss.
 */

export type FeedItemType = 'escalation' | 'followup' | 'opportunity';
export type ResolutionKind = 'dismiss' | 'done';

/** A durable write to attempt, in order, until one succeeds. */
export type Attempt = 'reject-action' | 'escalation-dismiss' | 'deal-signal-state';

export interface ResolutionPlan {
  /** Tried in order; the item is resolved when one succeeds. Empty = nothing to try. */
  attempts: readonly Attempt[];
  /** True when no durable write exists, so hiding the card is session-local. */
  sessionOnly: boolean;
}

export function planResolution(type: FeedItemType, kind: ResolutionKind, opts: { dealSignal?: boolean } = {}): ResolutionPlan {
  if (type === 'escalation') {
    return { attempts: ['escalation-dismiss'], sessionOnly: false };
  }
  if (type === 'followup') {
    // A deal card has exactly one backing store; see note 3 above.
    if (opts.dealSignal) return { attempts: ['deal-signal-state'], sessionOnly: false };
    // A mailbox followup is backed by either an action row or an escalation, and
    // the caller cannot tell which, so a dismissal tries both. A completion gets
    // only the escalation path — see note 1 above.
    return kind === 'done'
      ? { attempts: ['escalation-dismiss'], sessionOnly: false }
      : { attempts: ['reject-action', 'escalation-dismiss'], sessionOnly: false };
  }
  return { attempts: [], sessionOnly: true };
}
